import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Stimulsoft } from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer';
import type { GenericList } from './models/JobSharedInterfaces';
import type { DeliveryAddress } from './models/JobInterfaces';
import * as DateHelpers from './DateHelpers';
import ConfirmPopups from './ConfirmPopup';
import EditNoteModal from './EditNotes';
import ReopenJobData from './ReopenJobModal';
import AllocateExtendModal from './AllocateExtendModal';
import AllocateCancelModal from './AllocateCancelItem';
import JobScheduleAddNewData from './JobScheduleAddModal';
import JobScheduleEditPopup from './JobScheduleEditModal';
import JobScheduleExtendPopup from './JobScheduleExtendModal';
import JobScheduleReOpenPopup from './JobScheduleReOpenItemModal';
import JobQuoteDeliveryAddressData from './JobQuoteDeliveryAddressModal';
import JobScheduleAddPumpCleanPopup from './JobScheduleAddPumpClean';
import JobScheduleVoidModalData from './JobScheduleVoidModal';
import JobScheduleFencingModalData from './JobScheduleFencingModal';
import RunsheetEditJobPopup from './RunSheetEditJob';
import JobTabsData from './JobTabs';
import { GetQuery, PostQuery } from './Shared';

interface Schedule {
    id: number;
    jobQuoteItemId: number;
    companyId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemTypeId: number;
    isJobEvent: boolean;
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
    invoiceDescription: string;
    quantity: number;
    quantityScheduled: number;
    quantityType: string;
    quantityScheduledType: string;
    quantityTotal: number;
    quantityTotalType: string;
    quantityInvoiced: number;
    quantityInvoicedType: string;
    typeId: number;
    type: string;
    hirePeriodDays: number;
    defaultHirePeriod: number;
    customHirePeriod: boolean;
    frequencyId: number;
    locationId: number;
    status: string;
    statusId: number;
    allocatedTruck: string;
    scheduledDate: string;
    scheduledDateFormatted: string;
    scheduledDateEditable: string;
    onhireDate: string;
    onhireDateFormatted: string;
    onhireDateEditable: string | null;
    offhireDate: string;
    offhireDateFormatted: string;
    offhireDateEditable: string | null;
    pickupDateFormatted: string;
    lastHireDateFormatted: string;
    trolley: boolean;
    price: number;
    initial: number;
    travel: number;
    cleaningFee: number;
    trenchFee: number;
    overhireRate: number;
    overhireRateFormatted: string;
    accountTypeId: number;
    unitNumber: string;
    purchaseOrderNumber: string;
    purchaseOrderNumberFuel: string;
    notes: string;
    runsheetNotes: string;
    allowExtend: boolean;
    lastHireDate: string;
    allowReopen: boolean;
    allowDateChange: boolean;       //if user has permission to change dates and there is no vehicle assigned to this job
    allowDelete: boolean;
    allowOnOffHireDateChange: boolean;  //can't update if job has been invoiced
    isKwikstage: boolean;
    vehicleId: number;
    kwikstageItems: ReopenJobKwikstageItem[];
    isEws: boolean;
    isVoid: boolean;
    isFence: boolean;
    isEvent: boolean;
    manualItem: boolean;
    canEdit: boolean;
    canEditOverhire: boolean;
    extrasNote: string;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    pending: boolean;
    paid: boolean;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface ReopenJob {
    rowIndex: number;
    id: number;
    itemId: number;
    item: string;
    actualQuantity: number;
    quantity: number;
    rescheduleQuantity: number;
    schedulePickup: boolean;
    offhireDate: string;
    scheduleDate: string;
    subId: string;
    isKwikstage: boolean;
    kwikstageItems: ReopenJobKwikstageItem[];
}

interface ReopenJobKwikstageItem {
    id: number;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    starterBay: string;
    liftId: number;
    lift: string;
    baysTotal: number;
    bays: number;
    newBays: number;
    rescheduleBays: number;
}

interface CancelDetails {
    rowIndex: number;
    id: number;
    itemId: number;
    item: string;
    cancelReasonId: number;
}

const JobScheduleData = () => {
    let navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [jobTypeId, setJobTypeId] = React.useState(0);
    const [jobStatusId, setJobStatusId] = React.useState(0);
    const [schedule, setSchedule] = React.useState<Schedule[]>([]);
    const [showSave, setShowSave] = React.useState(false);
    const [customerId, setCustomerId] = React.useState(0);
    const [siteAddress, setSiteAddress] = React.useState("");
    const [siteAddressDuration, setSiteAddressDuration] = React.useState(0);
    const [showCancelledJobs, setShowCancelledJobs] = React.useState(false);
    const [showAddPumpClean, setShowAddPumpClean] = React.useState(false);
    const [quantityTotal, setQuantityTotal] = React.useState(0);
    const [showEditNotesModal, setShowEditNotesModal] = React.useState(false);
    const [showReopenModal, setShowReopenModal] = React.useState(false);
    const [showReopenDunnyModal, setShowReopenDunnyModal] = React.useState(false);
    const [reopenJob, setReopenJob] = React.useState<ReopenJob>({
        rowIndex: 0,
        id: 0,
        itemId: 0,
        item: "",
        actualQuantity: 0,
        quantity: 0,
        rescheduleQuantity: 0,
        schedulePickup: false,
        offhireDate: "",
        scheduleDate: "",
        isKwikstage: false,
        subId: "",
        kwikstageItems: []
    });
    const [showExtendModal, setShowExtendModal] = React.useState(false);
    const [showDunnyExtendModal, setShowDunnyExtendModal] = React.useState(false);
    const [showAddNewModal, setShowAddNewModal] = React.useState(false);
    const [frequencyOptions, setFrequencyOptions] = React.useState<GenericList[] > ([]);
    const [needsPickup, setNeedsPickup] = React.useState(false);
    const [showProformaInvoice, setShowProformaInvoice] = React.useState(false);
    const [showEditSimpleJobModal, setShowEditSimpleJobModal] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [itemIndex, setItemIndex] = React.useState(-1);
    const [showCancelModal, setShowCancelModal] = React.useState(false);
    const [cancelDetails, setCancelDetails] = React.useState<CancelDetails>({
        id: 0,
        rowIndex: 0,
        itemId: -1,
        item: "",
        cancelReasonId: -1
    });
    const [cancelOptions, setCancelOptions] = React.useState<GenericList[]>([]);
    const [canEditOverhireRate, setCanEditOverhireRate] = React.useState(false);
    const [canUpdateJobsToPaid, setCanUpdateJobsToPaid] = React.useState(false);
    const [showOverhireRate, setShowOverhireRate] = React.useState(false);
    const [scheduleId, setScheduleId] = React.useState(0);
    const [jobQuoteItemId, setJobQuoteItemId] = React.useState(0);
    const [accountTypeId, setAccountTypeId] = React.useState(0);
    const [showDelAddress, setShowDelAddress] = React.useState(false);
    const [deliveryAddress, setDeliveryAddress] = React.useState<DeliveryAddress>({
        deliveryAddress: "",
        deliveryAddressGooglePlaceId: "",
        deliveryAddressLatitude: 0,
        deliveryAddressLongitude: 0
    });
    const [invoiceDescription, setInvoiceDescription] = React.useState("");
    const [isJobEvent, setIsJobEvent] = React.useState(false);
    const [showVoidGearList, setShowVoidGearList] = React.useState(false);
    const [showFencingDetails, setShowFencingDetails] = React.useState(false);
    const [customerNewFuelInvoice, setCustomerNewFuelInvoice] = React.useState(false);
    const [saveDisabled, setSaveDisabled] = React.useState(false);        

    React.useEffect(() => {
        window.scrollTo(0, 0);
        getJobSchedule(false);
    }, []);

    const getJobSchedule = async (showCancelledJobs: boolean) => {
        try {
            const apiUrl = 'GetJobSchedule?JobId=' + id + '&ShowCancelledJobs=' + showCancelledJobs;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setSystemCompanyId(result.data.systemCompanyId);
                setJobTypeId(result.data.jobTypeId);
                setJobStatusId(result.data.jobStatusId);
                setSchedule(result.data.jobSchedule);
                setShowSave(result.data.showSave);
                setCustomerId(result.data.customerId);
                setSiteAddress(result.data.siteAddress);
                setSiteAddressDuration(result.data.siteAddressDuration);
                setFrequencyOptions(result.data.frequencyOptions);
                setNeedsPickup(result.data.needsPickup);
                setCanEditOverhireRate(result.data.canEditOverhireRate);
                setCanUpdateJobsToPaid(result.data.canUpdateJobsToPaid);
                setIsJobEvent(result.data.isJobEvent);
                setCustomerNewFuelInvoice(result.data.customerNewFuelInvoice);
                setLoading(false);
            }
        } catch (e) {
            toast.error("Error getting data!");
        }
    }

    const handleShowCancelledChange = (e: any) => {
        setShowCancelledJobs(prevState => !prevState);
        getJobSchedule(e.target.checked);
    }

    const onPaidCheckboxChange = (index: number, e: any) => {
        let sched = schedule;
        let item = sched[index];
        savePaidJob(item.id, e.target.checked);
    }

    const savePaidJob = async (scheduleId: number, paid: boolean) => {
        var saveJobToPaid = {
            scheduleId: scheduleId,
            paid: paid
        };

        var jsonItem = JSON.stringify(saveJobToPaid);

        const apiUrl = "SaveScheduleJobToPaid";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Job Updated To Paid");

            //reload the screen
            getJobSchedule(showCancelledJobs);
        };
    }

    const onScheduleDateCellChange = (index: number, e: any) => {
        let sched = [...schedule];
        let item = sched[index];
        item.scheduledDateEditable = e.target.value;
        item.isDirty = true;
        setSchedule(sched);
    }

    const onPurchaseOrderNumberChange = (index: number, e: any) => {
        let sched = [...schedule];
        let item = sched[index];
        item.purchaseOrderNumber = e.target.value;
        item.isDirty = true;
        setSchedule(sched);
    }

    const onPurchaseOrderNumberFuelChange = (index: number, e: any) => {
        let sched = [...schedule];
        let item = sched[index];
        item.purchaseOrderNumberFuel = e.target.value;
        item.isDirty = true;
        setSchedule(sched);
    }

    const onUnitNumberChange = (index: number, e: any) => {
        let sched = [...schedule];
        let item = sched[index];
        item.unitNumber = e.target.value;
        item.isDirty = true;
        setSchedule(sched);
    }

    const onOnOffDateCellChange = (index: number, e: any) => {
        let sched = [...schedule];
        let item = sched[index];
        item.isDirty = true;
        if (item.typeId === 1) {
            item.onhireDateEditable = e.target.value;
        } else if (item.typeId === 2) {
            item.offhireDateEditable = e.target.value;
        }
        setSchedule(sched);
    }

    //ADD NEW ITEM
    const add = () => {
        setShowAddNewModal(true);
    }

    const hideAddNew = () => {
        setShowAddNewModal(false);
    }

    const saveNewItem = () => {
        setShowAddNewModal(false);

        getJobSchedule(showCancelledJobs);
    }

    //END ADD NEW ITEM

    //EDIT OVERHIRE RATE
    const editOverhireRate = (index: number, e: any) => {
        e.preventDefault();
        let sched = schedule[index];
        setItemIndex(index);
        setShowOverhireRate(true);
        setScheduleId(sched.id);
    }

    const hideOverhireRate = () => {
        setItemIndex(-1);
        setShowOverhireRate(false);
        setScheduleId(0);
    }

    const saveOverhireRate = () => {
        setItemIndex(-1);
        setShowOverhireRate(false);
        setScheduleId(0);
        getJobSchedule(showCancelledJobs);
    }

    //END OVERHIRE RATE

    //NOTES
    const hideEditNotesModal = () => {
        setShowEditNotesModal(false);
        setScheduleId(-1);
        setItemIndex(-1);
    }

    const editNotes = (e: any, index: number) => {
        e.preventDefault();
        var scheduleItems = schedule;
        var scheduleItem = scheduleItems[index];
        setShowEditNotesModal(true);
        setScheduleId(scheduleItem.id);
        setItemIndex(index);
    }

    const saveNotes = (scheduleNotes: string, timestamp: Uint8Array) => {
        var scheduleItems = [...schedule];
        var scheduleItem = scheduleItems[itemIndex];
        scheduleItem.notes = scheduleNotes;
        scheduleItem.rowVersionTimestamp = timestamp;

        setShowEditNotesModal(false);
        setScheduleId(-1);
        setItemIndex(-1);
    }
    //END NOTES

    //REOPEN 
    const reopenItem = (e: any, index: number) => {
        e.preventDefault();
        var scheduleItems = schedule;
        var scheduleItem = scheduleItems[index];

        if (systemCompanyId === 3) {
            //reopen a cancelled pump and clean job or an install/pickup that was an accident
            setShowReopenDunnyModal(true);
            setScheduleId(scheduleItem.id);
        } else {
            var updateReopenJob: ReopenJob = {
                id: scheduleItem.id,
                rowIndex: index,
                itemId: scheduleItem.itemId,
                item: scheduleItem.item,
                actualQuantity: scheduleItem.quantity,
                quantity: 0,
                rescheduleQuantity: 0,
                schedulePickup: false,
                offhireDate: "",
                scheduleDate: "",
                isKwikstage: scheduleItem.isKwikstage,
                subId: "",
                kwikstageItems: scheduleItem.kwikstageItems
            };

            setReopenJob(updateReopenJob);
            setShowReopenModal(true);
        }
    }

    const updatedReopenData = (updatedJob: ReopenJob) => {
        setReopenJob(updatedJob);
    }

    const hideReopenModal = () => {
        var resetJob = resetReopenJob();
        setShowReopenModal(false);
        setShowReopenDunnyModal(false);
        setScheduleId(0);
        setReopenJob(resetJob);
    }

    const resetReopenJob = () => {
        var resetJob: ReopenJob = {
            id: 0,
            rowIndex: 0,
            itemId: 0,
            item: "",
            actualQuantity: 0,
            quantity: 0,
            rescheduleQuantity: 0,
            schedulePickup: false,
            offhireDate: "",
            scheduleDate: "",
            isKwikstage: false,
            subId: "",
            kwikstageItems: []
        };
        return resetJob;
    }

    const saveReopenJob = () => {
        if (systemCompanyId === 3) {
            //saved on the modal popup so just close here and refresh screen
            setShowReopenDunnyModal(false);
            setScheduleId(0);
            getJobSchedule(showCancelledJobs);
        } else {
            reopenScheduledJob();
        }
    }

    const reopenScheduledJob = async () => {
        try {
            var jsonItem = JSON.stringify(reopenJob);

            const apiUrl = "SaveScheduleReopenJob";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    toast.success("Reopen Job Saved");
                    var resetJob = resetReopenJob();
                    setReopenJob(resetJob);
                    setShowReopenModal(false);
                    //reload the screen
                    getJobSchedule(showCancelledJobs);
                }
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }
    //END REOPEN

    //Extend Modal
    const extendJob = (e: any, index: number) => {
        e.preventDefault();
        var scheduleList = schedule;
        var scheduleItem = scheduleList[index];

        if (systemCompanyId === 3) {
            //dunny extend
            openDunnyExtend(scheduleItem.id, scheduleItem.jobQuoteItemId, scheduleItem.accountTypeId);
        } else {
            //scaffold extend
            openScaffoldExtend(scheduleItem.id, scheduleItem.jobQuoteItemId);
        }
    }

    //scaffold extend
    const openScaffoldExtend = (scheduleId: number, jobQuoteItemId: number) => {
        setShowExtendModal(true);
        setScheduleId(scheduleId);
        setJobQuoteItemId(jobQuoteItemId);
    }

    const hideExtendModal = () => {
        setShowExtendModal(false);
        setScheduleId(0);
        setJobQuoteItemId(0);
    }

    const saveExtendData = () => {
        setShowExtendModal(false);
        setScheduleId(0);
        setJobQuoteItemId(0);
        setShowCancelledJobs(false);
        toast.success("Item Extended Successfully");
        getJobSchedule(false);
    }

    //dunny extend
    const openDunnyExtend = (scheduleId: number, jobQuoteItemId: number, acctTypeId: number) => {
        setShowDunnyExtendModal(true);
        setScheduleId(scheduleId);
        setJobQuoteItemId(jobQuoteItemId);
        setAccountTypeId(acctTypeId);
    }

    const hideDunnyExtend = () => {
        setShowDunnyExtendModal(false);
        setScheduleId(0);
        setJobQuoteItemId(0);
        setAccountTypeId(0);
    }

    const saveDunnyExtend = () => {
        if (accountTypeId === 1) {
            //open job request for prepaid customers so payment can be taken
            printExtendJob(jobQuoteItemId);
        }
        setShowDunnyExtendModal(false);
        setScheduleId(0);
        setJobQuoteItemId(0);
        setAccountTypeId(0);
        setShowCancelledJobs(false);
        getJobSchedule(false);
    }

    const printExtendJob = (jobQuoteItemId: number) => {
        var url = "/job/" + id + "/report/JobExtendRequest/" + jobQuoteItemId;
        window.open(url, '_blank');
    }
    //End Extend Modal

    //ADD MAINT PUMP & CLEAN TO TOILET
    const addPumpClean = (e: any, index: number) => {
        e.preventDefault();

        var items = schedule;
        var item = items[index];
        setItemIndex(index);
        setScheduleId(item.id);
        setShowAddPumpClean(true);
        setQuantityTotal(item.quantityTotal);
    }

    const hidePumpClean = () => {
        setItemIndex(-1);
        setScheduleId(0);
        setShowAddPumpClean(false);
        setQuantityTotal(0);
    }

    const savePumpClean = () => {
        setItemIndex(-1);
        setScheduleId(0);
        setShowAddPumpClean(false);
        getJobSchedule(false);
        setQuantityTotal(0);
    }
    //END ADD MAINT PUMP & CLEAN TO TOILET

    //DELIVERY ADDRESS
    const editDelAddress = (e: any, index: number) => {
        e.preventDefault();

        var items = schedule;
        var item = items[index];

        var delAddr: DeliveryAddress = {
            deliveryAddress: item.deliveryAddress,
            deliveryAddressGooglePlaceId: item.deliveryAddressGooglePlaceId,
            deliveryAddressLatitude: item.deliveryAddressLatitude,
            deliveryAddressLongitude: item.deliveryAddressLongitude
        };
        setItemIndex(index);
        setScheduleId(item.id);
        setShowDelAddress(true);
        setDeliveryAddress(delAddr);
        setInvoiceDescription(item.invoiceDescription);
    }

    const hideDelAddress = () => {
        var delAddr = resetDeliveryAddress();
        setItemIndex(-1);
        setScheduleId(0);
        setShowDelAddress(false);
        setDeliveryAddress(delAddr);
        setInvoiceDescription("");
    }

    const updateDelAddress = (updatedDeliveryAddress: DeliveryAddress) => {
        setDeliveryAddress(updatedDeliveryAddress);
    }

    const clearDelAddress = () => {
        var delAddress = resetDeliveryAddress();
        setDeliveryAddress(delAddress);
    }

    const resetDeliveryAddress = () => {
        var delAddr: DeliveryAddress = {
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0
        };
        return delAddr;
    }

    const updateInvDesc = (updatedInvoiceDescription: string) => {
        setInvoiceDescription(updatedInvoiceDescription);
    }

    const updateTrolley = (_updatedTrolley: boolean) => {
        //not used
    }

    const saveDelAddress = () => {
        saveDeliveryAddress();
    }

    const saveDeliveryAddress = async () => {
        var items = schedule;
        var item = items[itemIndex];

        if (deliveryAddress.deliveryAddress != "") {
            item.deliveryAddress = deliveryAddress.deliveryAddress;
            item.deliveryAddressGooglePlaceId = deliveryAddress.deliveryAddressGooglePlaceId;
            item.deliveryAddressLatitude = deliveryAddress.deliveryAddressLatitude;
            item.deliveryAddressLongitude = deliveryAddress.deliveryAddressLongitude;
        }
        item.invoiceDescription = invoiceDescription;

        var saveDelAddress = {
            scheduleId: item.id,
            jobQuoteItemId: item.jobQuoteItemId,
            deliveryAddress: deliveryAddress.deliveryAddress,
            googlePlaceId: deliveryAddress.deliveryAddressGooglePlaceId,
            deliveryAddressLatitude: deliveryAddress.deliveryAddressLatitude,
            deliveryAddressLongitude: deliveryAddress.deliveryAddressLongitude,
            invoiceDescription: invoiceDescription,
            trolley: item.trolley
        };

        //save
        try {
            var jsonItem = JSON.stringify(saveDelAddress);

            const apiUrl = "SaveDeliveryAddress";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Saved Successfully");
                var delAddress = resetDeliveryAddress();
                setItemIndex(-1);
                setScheduleId(0);
                setShowDelAddress(false);
                setDeliveryAddress(delAddress);
                setInvoiceDescription("");
                getJobSchedule(showCancelledJobs);
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }

    //END DELIVERY ADDRESS

    const printSwms = (e: any, index: number) => {
        e.preventDefault();

        var scheduleList = schedule;
        var scheduleItem = scheduleList[index];

        downloadSwms(scheduleItem.scheduledDate, scheduleItem.vehicleId, Number(id));
    }

    const downloadSwms = async (scheduleDate: string, vehicleId: number, jobId: number) => {
        try {
            const baseURL = window.location.origin;
            const apiUrl = 'GetScheduleSwms?ScheduleDate=' + scheduleDate + "&VehicleId=" + vehicleId + "&JobId=" + jobId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var swms = result.data.reportData;
                if (swms.length > 0) {
                    for (let x = 0; x < swms.length; x++) {
                        let report = new Stimulsoft.Report.StiReport();
                        let reportFile = Stimulsoft.System.IO.File.getFile(baseURL + "/reports/" + result.data.reportFileName);
                        report.load(reportFile);

                        report.dictionary.dataStore.clear();

                        let reportData = JSON.stringify(swms[x]);
                        //Dataset created
                        let dataSet = new Stimulsoft.System.Data.DataSet("data");
                        dataSet.readJson(reportData)
                        report.regData(dataSet.dataSetName, "", dataSet);
                        report.reportName = result.data.reportName;
                        report.dictionary.connect(false);
                        report.dictionary.synchronize();

                        //add relationships between data
                        for (var i = 0; i < result.data.reportRelationships.length; i++) {
                            var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(result.data.reportRelationships[i].sourceName, result.data.reportRelationships[i].name, result.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(result.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(result.data.reportRelationships[i].childTableName), [result.data.reportRelationships[i].parentFieldName], [result.data.reportRelationships[i].childFieldName]);
                            report.dictionary.relations.add(dataRelation);
                            report.dictionary.synchronize();
                        }

                        let fileName = result.data.reportName + "-" + (x + 1);

                        //report.render();
                        report.renderAsync(() => {
                            report.exportDocumentAsync(function (pdfData: any) {
                                // Save data to file
                                Stimulsoft.System.StiObject.saveAs(pdfData, fileName + ".pdf", "application/pdf");
                            }, Stimulsoft.Report.StiExportFormat.Pdf);
                        });
                    }
                }
            }
        } catch (e) {
            toast.error("Error getting SWMS!");
        }
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        let items = schedule.filter(x => x.isDirty);    //only validate items that have changed as they are the only ones to be saved
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (!DateHelpers.isValidDate(items[i].scheduledDateEditable)) {
                newErrorString += " Valid Schedule Date, ";
                error = true;
            }

            if (!items[i].manualItem) {
                if (items[i].typeId === 1 && !DateHelpers.isValidDate(items[i].onhireDateEditable)) {
                    newErrorString += " Valid Onhire Date, ";
                    error = true; 
                } else if (items[i].typeId === 1 && items[i].onhireDateEditable && !DateHelpers.compareDate(items[i].scheduledDateEditable, items[i].onhireDateEditable ?? "")) {
                    newErrorString += " Onhire Date >= Schedule Date, ";
                    error = true;
                }
                if (items[i].typeId === 2 && !DateHelpers.isValidDate(items[i].offhireDateEditable)) {
                    newErrorString += " Valid Offhire Date, ";
                    error = true;
                } else if (items[i].typeId === 2 && items[i].offhireDateEditable && !DateHelpers.compareDate(items[i].offhireDateEditable ?? "", items[i].scheduledDateEditable)) {
                    newErrorString += " Offhire Date <= Schedule Date, ";
                    error = true;
                }
            }

            if (newErrorString != "") {
                errorString += "\n";
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (error) {
            formIsValid = false;
            errorList["scheduleItems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const saveDates = async () => {
        if (validate()) {
            let items = schedule.filter(x => x.isDirty); //only save items that have changed

            if (items.length > 0) {
                try {
                    var saveSchedDates = {
                        items: items
                    };

                    //cater for null dates for API
                    for (var i = 0; i < items.length; i++) {
                        var dateItem = items[i];
                        dateItem.onhireDateEditable = dateItem.onhireDateEditable === "" ? null : dateItem.onhireDateEditable;
                        dateItem.offhireDateEditable = dateItem.offhireDateEditable === "" ? null : dateItem.offhireDateEditable;
                    }

                    var jsonItem = JSON.stringify(saveSchedDates);

                    const apiUrl = "SaveScheduleDates";
                    var result = await PostQuery(apiUrl, jsonItem);
                    if (result.error) {
                        toast.error("Error saving item tasks!");
                    } else {
                        getJobSchedule(showCancelledJobs);
                        toast.success("Scheduled Job Saved");
                    }
                } catch (e) {
                    toast.error("Error saving dates!");
                }
            } else {
                toast.info("No items have changed!");
            }
        } else {
            toast.error("Please fix validation errors and try Saving again!");
        }
    }

    //CREATE PROFORMA INVOICE
    const invoiceJob = (e: any) => {
        e.preventDefault();
        setShowProformaInvoice(true);
    }

    const hideProformaInvoice = () => {
        setShowProformaInvoice(false);
    }

    const saveProformaInvoice = () => {
        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);

        saveInvoice();
    }

    const saveInvoice = async () => {
        //there could be multiple items to create an invoice for
        var scheduleIds: number[] = [];
        var jobs = schedule;

        for (var i = 0; i < jobs.length; i++) {
            if (jobs[i].status != "Cancelled" && jobs[i].status != "Invoiced" && jobs[i].typeId === 1) {
                scheduleIds.push(jobs[i].id);
            }
        }
        
        var saveItem = {
            scheduleIds: scheduleIds,
            jobId: id
        };

        try {
            var jsonItem = JSON.stringify(saveItem);

            const apiUrl = "CreateProformaInvoice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Initial Invoice Created Successfully");
                setShowProformaInvoice(false);
                setSaveDisabled(false);
                getJobSchedule(false);
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }
    //END CREATE PROFORMA INVOICE

    //EDIT JOB PRICE
    const editSimpleJob = (e: any, jobIndex: number) => {
        e.preventDefault();
        var job = schedule[jobIndex];
        setShowEditSimpleJobModal(true);
        setScheduleId(job.id);
        setItemIndex(jobIndex);
    }

    const hideEditJobModal = () => {
        setShowEditSimpleJobModal(false);
        setScheduleId(0);
        setItemIndex(-1);
    }

    const saveEditSimpleJob = (quantityScheduled: number, quantityTotal: number, item: string, itemShortcut: string, timestamp: Uint8Array) => {
        var jobs = [...schedule];
        var simpleJob = jobs[itemIndex];
        simpleJob.quantityScheduled = quantityScheduled;
        simpleJob.quantityTotal = quantityTotal;
        simpleJob.item = item;
        simpleJob.itemShortcut = itemShortcut;
        simpleJob.rowVersionTimestamp = timestamp;

        setSchedule(jobs);
        setShowEditSimpleJobModal(false);
        setScheduleId(0);
        setItemIndex(-1);
        getJobSchedule(false);
    }
    //END EDIT JOB PRICE

    //CANCEL ITEM - only for Dunnys and only if the job hasn't been allocated to a truck
    const cancelItem = (e: any, index: number) => {
        e.preventDefault();
        setShowDelete(true);
        setItemIndex(index);
    }

    const hideDelete = () => {
        setShowDelete(false);
        setItemIndex(-1);
    }

    const saveDelete = () => {
        //need to popup reason for cancelling
        setShowDelete(false);
        getCancelReasons();
    }

    const getCancelReasons = async () => {
        try {
            let errorList: any = {};
            var scheduleItem = schedule[itemIndex];
            const apiUrl = 'CheckCancelOptions?Id=' + scheduleItem.id + '&JobId=' + id + '&JobQuoteItemId=' + scheduleItem.jobQuoteItemId + '&ScheduleType=' + scheduleItem.typeId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var cancelReasons = result.data.cancelReasons;
                var cancelOption = result.data.cancelOption;

                if (cancelOption === 1) {
                    //show popup to cancel
                    let cancelDets: CancelDetails = {
                        rowIndex: itemIndex,
                        id: scheduleItem.id,
                        itemId: scheduleItem.itemId,
                        item: scheduleItem.item,
                        cancelReasonId: -1
                    };

                    setShowCancelModal(true);
                    setCancelDetails(cancelDets);
                    setCancelOptions(cancelReasons)
                } else if (cancelOption === 2) {
                    //this job has been split into multiple jobs so Ops MUST Cancel
                    toast.error("Cannot cancel this item as the job has been split over multiple trucks. Ops MUST cancel!");
                    errorList["scheduleItems"] = "Cannot cancel this item as the job has been split over multiple trucks. Ops MUST cancel!";
                    setErrors(errorList);
                    setItemIndex(-1);
                } else if (cancelOption === 3) {
                    //can't cancel
                    toast.error("Cannot cancel this item, see error on screen!");
                    var errorString = "Row " + (itemIndex + 1) + " cannot be cancelled as the remaining qty must be installed to create the Initial Invoice.";
                    errorList["scheduleItems"] = errorString;
                    setErrors(errorList);
                    setItemIndex(-1);
                } else {
                    toast.error("Cannot cancel this item as the customer is not setup for Overhire. Extend job instead.");
                    errorList["scheduleItems"] = "Cannot cancel this item as the customer is not setup for Overhire. Extend job instead.";
                    setErrors(errorList);
                    setItemIndex(-1);
                }
            }
        } catch (e) {
            toast.error("Error getting data!");
        }
    }

    const resetCancelDetails = () => {
        let cancelDetails: CancelDetails = {
            rowIndex: 0,
            id: 0,
            itemId: -1,
            item: "",
            cancelReasonId: -1
        };
        return cancelDetails;
    }

    const hideCancelModal = () => {
        var cancelDets = resetCancelDetails();

        setShowCancelModal(false);
        setCancelDetails(cancelDets);
        setCancelOptions([]);
    }

    const updateCancelData = (updatedCancelDetails: CancelDetails) => {
        setCancelDetails(prevState => ({ ...prevState, cancelReasonId: updatedCancelDetails.cancelReasonId }));
    }

    const saveCancel = () => {
        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);
        saveCancelItem();
    }

    const saveCancelItem = async () => {
        //cancel the item 
        var scheduleItem = schedule[itemIndex];
        if (scheduleItem) {
            var cancelItem = {
                scheduleId: scheduleItem.id,
                jobId: id,
                itemId: scheduleItem.itemId,
                item: scheduleItem.item,
                cancelReasonId: cancelDetails.cancelReasonId,
                cancelJobStatus: true,      //set the job item status to cancelled
                manualItem: scheduleItem.manualItem
            };

            try {
                var jsonItem = JSON.stringify(cancelItem);

                const apiUrl = "CancelItem";
                var result = await PostQuery(apiUrl, jsonItem);
                if (result.error) {
                    toast.error("Error saving data!");
                } else {
                    toast.success("Item Cancelled Successfully");
                    var cancelDets = resetCancelDetails();
                    setShowCancelModal(false);
                    setCancelDetails(cancelDets);
                    setCancelOptions([]);
                    setSaveDisabled(false);
                    getJobSchedule(showCancelledJobs);
                }
            } catch (e) {
                toast.error("Error saving job!");
            }     
        }
    }

    //END CANCEL ITEM

    //VOID GEAR LIST
    const openVoidGearList = (e: any, index: number) => {
        e.preventDefault();

        var items = schedule;
        var item = items[index];

        setItemIndex(index);
        setJobQuoteItemId(item.jobQuoteItemId);
        setShowVoidGearList(true);
    }

    const hideVoidGearList = () => {
        setItemIndex(-1);
        setJobQuoteItemId(0);
        setShowVoidGearList(false);
    }

    const saveVoidGearList = () => {
        setItemIndex(-1);
        setJobQuoteItemId(0);
        setShowVoidGearList(false);
    }
    //END VOID GEARLIST

    //FENCING DETAILS
    const openFencingDetails = (e: any) => {
        e.preventDefault();
        setShowFencingDetails(true);
    }

    const hideFencingDetails = () => {
        setShowFencingDetails(false);
    }

    const saveFencingDetails = () => {
        setShowFencingDetails(false);
    }
    //END FENCING DETAILS

    const tabChanged = (url: string) => {
        navigate(url);
    }

    var editNotesModal = <EditNoteModal systemCompanyId={systemCompanyId} scheduleId={scheduleId} editDriverNotes={false} showDriverScheduleNotes={false} showEditNotesModal={showEditNotesModal} save={saveNotes} hideModal={hideEditNotesModal} />
    var reopenJobModal = <ReopenJobData reopenJob={reopenJob} showModal={showReopenModal} updateData={updatedReopenData} save={saveReopenJob} hideModal={hideReopenModal} />
    var dunnyReOpenModal = <JobScheduleReOpenPopup showModal={showReopenDunnyModal} scheduleId={scheduleId} hideModal={hideReopenModal} saveModal={saveReopenJob} />
    var extendModal = <AllocateExtendModal scheduleId={scheduleId} jobQuoteItemId={jobQuoteItemId} showExtendModal={showExtendModal} hideModal={hideExtendModal} save={saveExtendData} />
    var dunnyExtendModal = <JobScheduleExtendPopup showModal={showDunnyExtendModal} scheduleId={scheduleId} hideModal={hideDunnyExtend} saveModal={saveDunnyExtend} />
    var addNewModal = <JobScheduleAddNewData jobId={Number(id)} isEvent={isJobEvent} customerId={customerId} siteAddressDuration={siteAddressDuration} needsPickup={needsPickup} customerNewFuelInvoice={customerNewFuelInvoice} showModal={showAddNewModal} hideModal={hideAddNew} save={saveNewItem} />
    let confirmInvoice = <ConfirmPopups heading="Create Initial Invoice" text="Are you sure you want to create an initial invoice for this job?" showConfirmModal={showProformaInvoice} hideConfirmModal={hideProformaInvoice} saveConfirmModal={saveProformaInvoice} />
    let confirmDelete = <ConfirmPopups heading="Cancel Item?" text="Are you sure you want to cancel this item?" showConfirmModal={showDelete} hideConfirmModal={hideDelete} saveConfirmModal={saveDelete} />
    var cancelModal = <AllocateCancelModal cancelDetails={cancelDetails} cancelOptions={cancelOptions} showModal={showCancelModal} hideModal={hideCancelModal} updateData={updateCancelData} save={saveCancel} />
    var editOverhireRateModal = <JobScheduleEditPopup scheduleId={scheduleId} showModal={showOverhireRate} hideModal={hideOverhireRate} saveModal={saveOverhireRate} />
    let delAddressPopup = <JobQuoteDeliveryAddressData deliveryAddress={deliveryAddress} showDeliveryAddress={true} invoiceDescription={invoiceDescription} showInvoiceDescription={isJobEvent} trolleyRequired={false} showTrolley={false} readOnly={false} showModal={showDelAddress} hideModal={hideDelAddress} updateDeliveryAddress={updateDelAddress} clearDeliveryAddress={clearDelAddress} updateInvoiceDescription={updateInvDesc} updateTrolley={updateTrolley} saveModal={saveDelAddress} />
    let addPumpAndClean = <JobScheduleAddPumpCleanPopup scheduleId={scheduleId} frequencyOptions={frequencyOptions} quantityTotal={quantityTotal} showModal={showAddPumpClean} hideModal={hidePumpClean} saveModal={savePumpClean} />
    let editJobPopup = <RunsheetEditJobPopup showModal={showEditSimpleJobModal} scheduleId={scheduleId} fromRunsheet={false} systemCompanyId={systemCompanyId} hideModal={hideEditJobModal} saveModal={saveEditSimpleJob} />;
    let editVoidGearlist = <JobScheduleVoidModalData showModal={showVoidGearList} jobQuoteItemId={jobQuoteItemId} hideModal={hideVoidGearList} saveModal={saveVoidGearList} />
    let editFencingDetails = <JobScheduleFencingModalData showModal={showFencingDetails} jobId={Number(id)} hideModal={hideFencingDetails} saveModal={saveFencingDetails} />
    let minDate = DateHelpers.addDaysToCurrentDate(-30);
    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"schedule"} tabChanged={tabChanged} />;

    const renderDetails = (
        <div>
            {editNotesModal}
            {reopenJobModal}
            {extendModal}
            {dunnyExtendModal}
            {dunnyReOpenModal}
            {addNewModal}
            {confirmInvoice}
            {confirmDelete}
            {cancelModal}
            {editOverhireRateModal}
            {delAddressPopup}
            {addPumpAndClean}
            {editJobPopup}
            {editVoidGearlist}
            {editFencingDetails}
            {jobTab}
            <div>
                <div className="makeitflexspacebetween marginBottom10">
                    <label className="input-group" htmlFor="siteAddrExists">
                        <span className="label label--setsizesm">Show Cancelled Jobs?</span>
                        <input className="checkbox" type="checkbox" id="showCancelledJobs" name="showCancelledJobs" checked={showCancelledJobs} onChange={(e) => handleShowCancelledChange(e)}></input>
                    </label>
                    <div className={systemCompanyId === 3 && jobStatusId != 7 ? "makeitflex" : "hidden"}>
                        <button className={jobTypeId === 2 || jobTypeId === 3 ? "defaultbutton marginRight10" : "hidden"} type='button' title="Create an initial invoice for this job" onClick={invoiceJob}>Create Initial Invoice</button>
                        <button className="defaultbutton defaultbutton__small" type='button' title="Allows user to schedule in a new item for this job" onClick={add}>Add New</button>
                    </div>
                </div>
                <div className="marginLeft10 noteSize">* Items in red are pending payment or go-ahead sign-off.</div>
                <div className="marginLeft10 allocate__customerNoteText">* Items in dark red need Customer unlocked.</div>
                <div className="marginLeft10 allocate__customerReckonNoteText">* Items in blue need Customer linked to Reckon.</div>
                <div className="table__minHeight table__margin overflowAuto">
                    <table className="table table__large tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th className="table-input--minwidth100">Item</th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th>Paid</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Allocated</th>
                                <th className="table-input--minwidth">Qty Total</th>
                                <th className="table-input--minwidth">Qty Sched</th>
                                <th className="table-input--minwidth">Qty</th>
                                <th className="table-input--minwidth">Qty Inv</th>
                                <th>Overhire</th>
                                <th></th>
                                <th>On/Offhire Date</th>
                                <th>Scheduled Date</th>
                                <th>PO No.</th>
                                <th className={customerNewFuelInvoice ? "" : "hidden"}>Fuel PO No.</th>
                                <th>Unit/Water/Waste No.</th>
                                <th>Edit Job</th>
                                <th className="textaligncenter table-input--minwidth100">Notes</th>
                                <th className="textaligncenter">Extend</th>
                                <th className="textaligncenter">Re-open</th>
                                <th className="textaligncenter">SWMS</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Cancel</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedule.map((item, index) =>
                                <tr key={index}>
                                    <td className="hidden">{item.id}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        {item.item}
                                    </td>
                                    <td className="table__text--align">
                                        <a className={item.itemTypeId === 25 && item.frequencyId === 0 && (item.statusId === 4 || item.statusId === 6) ? "tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => addPumpClean(e, index)}>
                                            <span className="fa-solid fa-circle-plus edit--icon alignIconCenter"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Add maintenance pump & clean to toilet</span>
                                        </a>
                                    </td>
                                    <td className="table__text--align">
                                        <a className={item.statusId === 3 && !item.manualItem && (item.isEvent || item.isJobEvent) ? "tooltipitem tooltipitem__right alignIconCenter" + (item.deliveryAddress == "" ? "" : " makeitgreen") : "hidden"} href="#" onClick={(e) => editDelAddress(e, index)}>
                                            <span className="fas fa-edit copy--icon"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Add/Edit Delivery Address if different to Site Address</span>
                                        </a>
                                    </td>
                                    <td className="table__text--align">
                                        <a className={item.isVoid && item.statusId > 2 && item.statusId < 7 ? "tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => openVoidGearList(e, index)}>
                                            <span className="fas fa-edit edit--icon alignIconCenter"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Update the void gear list</span>
                                        </a>
                                        <a className={item.isFence && item.statusId > 2 && item.statusId < 8 ? "tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => openFencingDetails(e)}>
                                            <span className="fas fa-edit edit--icon alignIconCenter"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">View Fencing Details</span>
                                        </a>
                                    </td>
                                    <td className="table__text--align">
                                        <input type='checkbox' id="paid" name="paid" checked={item.paid} onChange={(e) => onPaidCheckboxChange(index, e)} disabled={!canUpdateJobsToPaid || item.statusId != 3 || item.accountTypeId != 1} />
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.type}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.status}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.allocatedTruck}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.quantityTotalType}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.quantityScheduledType}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.quantityType}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.quantityInvoicedType}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>{item.manualItem ? "" : item.overhireRateFormatted}</td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <a className={canEditOverhireRate && item.canEditOverhire && !item.manualItem && item.typeId === 1 ? "tooltipitem tooltipitem__right alignIconCenter" : "hidden"} href="#" onClick={(e) => editOverhireRate(index, e)}>
                                            <span className="fas fa-edit copy--icon"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Edit Overhire Rate (USE CAUTION)</span>
                                        </a>
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <input type='date' min={minDate} className={item.allowOnOffHireDateChange && item.typeId != 3 && !item.manualItem ? "form-control allocate__dateSize" : "hidden"} value={item.typeId === 1 ? item.onhireDateEditable ?? "" : item.offhireDateEditable ?? ""} onChange={(e) => onOnOffDateCellChange(index, e)} />
                                        {item.allowOnOffHireDateChange ? "" : item.typeId === 1 ? item.onhireDateFormatted : item.offhireDateFormatted}
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <input type='date' min={minDate} className={item.allowDateChange ? "form-control allocate__dateSize" : "hidden"} value={item.scheduledDateEditable} onChange={(e) => onScheduleDateCellChange(index, e)} />
                                        {item.allowDateChange ? "" : item.scheduledDateFormatted}
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <input type="text" maxLength={25} className={item.statusId != 8 && !item.manualItem && (item.typeId === 1 || item.typeId === 2) ? "form-control allocate__size130" : "hidden"} value={item.purchaseOrderNumber} onChange={(e) => onPurchaseOrderNumberChange(index, e)} />
                                        {item.statusId != 8 ? "" : item.purchaseOrderNumber}
                                    </td>
                                    <td className={customerNewFuelInvoice ? "table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "") : "hidden"}>
                                        <input type="text" maxLength={25} className={item.statusId != 8 && !item.manualItem && (item.typeId === 1 || item.typeId === 2) ? "form-control allocate__size130" : "hidden"} value={item.purchaseOrderNumberFuel} onChange={(e) => onPurchaseOrderNumberFuelChange(index, e)} />
                                        {item.statusId != 8 ? "" : item.purchaseOrderNumberFuel}
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <input type="text" maxLength={25} className={item.statusId != 8 && !item.manualItem && (item.typeId === 1 || item.typeId === 2) ? "form-control allocate__dateSize" : "hidden"} value={item.unitNumber} onChange={(e) => onUnitNumberChange(index, e)} />
                                        {item.statusId != 8 ? "" : item.unitNumber}
                                    </td>
                                    <td className="table__text--align">
                                        <a className={item.typeId === 1 && !item.manualItem && item.canEdit && item.companyId === 3 ? "" : "hidden"} href="#" onClick={(e) => editSimpleJob(e, index)}>
                                            <span className="fas fa-folder-open copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Edit job if changes to plan (USE CAUTION)</span></span>
                                        </a>
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <a className={item.notes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, index)}>
                                            <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.substring(0, 20) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{item.notes}</span>
                                            </div>
                                        </a>
                                        <a className={item.notes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, index)}>
                                            <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                        </a>
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <a className={item.allowExtend && item.status != "Cancelled" && item.status != "Picked Up" ? "" : "hidden"} data-toggle="modal" data-target="#extendModal" href="#" onClick={(e) => extendJob(e, index)}>
                                            <span className="fas fa-arrow-right edit--icon alignIconCenter"></span>
                                        </a>
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <a className={item.allowReopen ? "" : "hidden"} href="#" onClick={(e) => reopenItem(e, index)}>
                                            <span className="fas fa-folder-open edit--icon alignIconCenter"></span>
                                        </a>
                                    </td>
                                    <td className={"table__text--align" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : item.customerUpdatesRequired ? " allocate__customerText" : item.pending ? " allocate__redText" : "")}>
                                        <div className={systemCompanyId === 2 ? "" : "hidden"}>
                                            <a className={item.vehicleId === 0 ? "hidden" : ""} href="#" onClick={(e) => printSwms(e, index)}>
                                                <span className="fas fa-file-shield edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                    <td className={systemCompanyId === 3 ? "" : "hidden"}>
                                        <div className={item.allowDelete ? "delete--tablecell" : "hidden"}>
                                            <a className="makeitred" href="#" onClick={(e) => cancelItem(e, index)}>
                                                <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {errors["scheduleItems"] ?
                        (errors["scheduleItems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
                <div className="colAlignRight marginBottom10">
                    <button className={showSave ? "defaultbutton defaultbutton__large" : "hidden"} type="button" onClick={saveDates}>Save</button>
                </div>
            </div>
        </div>
    )
    
    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobScheduleData;